import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FullViewTableCard, SimpleCard } from "../components/cards";
import Row from "../components/general/row";
import Column from "../components/general/column";
import { PageLoader } from "../components/general/loaders";
import { DateRangeFilter } from "../components/cards/dateRangeFilter";
import { SalesMonthFilter } from "../components/cards/salesMonthFilter";
import { useAuth } from "../context/authContext";
import { getClientByNetwork } from "../api/clients-api";
import { payment_axi } from "../context/authContext";
import { formatCurrency, formatDateTime } from "../utils.js";
import { downloadAccountStatementPdf } from "../utils/accountStatementPdf.js";
import {
  ArrowLeft,
  ArrowDownLeft,
  ArrowUpRight,
  CircleDollarSign,
  Share,
} from "lucide-react";
import "react-datepicker/dist/react-datepicker.css";

const formatTransactionType = (value) => {
  if (!value) return "-";
  if (value === "internal_power_purchase") return "Power Purchase";
  if (value === "auto_recovered_debt") return "Recovered Debt";
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const WalletStatement = () => {
  const navigate = useNavigate();
  const { user, loading, NETWORK } = useAuth();
  const [client, setClient] = useState();
  const [walletAccount, setWalletAccount] = useState();
  const [walletAccountLoading, setWalletAccountLoading] = useState(false);
  const [accountStatement, setAccountStatement] = useState([]);
  const [statementLoading, setStatementLoading] = useState(false);
  const [statementInitialized, setStatementInitialized] = useState(false);
  const [filterType, setFilterType] = useState("month");
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [rangeStartDate, setRangeStartDate] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [rangeEndDate, setRangeEndDate] = useState(new Date());
  const [appliedFilter, setAppliedFilter] = useState({
    type: "month",
    month: new Date(),
  });

  const fetchProfileData = async (network) => {
    try {
      const response = await getClientByNetwork(network);
      setClient(response.data);
    } catch (error) {
      console.error("Error fetching profile data:", error);
    }
  };

  const fetchWalletAccount = async (clientId) => {
    try {
      setWalletAccountLoading(true);
      const response = await payment_axi.get(`/api/accounts/get-account-by-client-id/${clientId}`);
      setWalletAccount(response?.data);
    } catch (error) {
      console.error("Error fetching wallet account:", error);
    } finally {
      setWalletAccountLoading(false);
    }
  };

  const fetchAccountStatement = async (accountId) => {
    try {
      setStatementLoading(true);
      const response = await payment_axi.get(`/api/account-reports/get-account-statement/${accountId}`);
      response?.data ? setAccountStatement(response.data) : setAccountStatement([]);
    } catch (error) {
      console.error("Error fetching account statement:", error);
    } finally {
      setStatementInitialized(true);
      setStatementLoading(false);
    }
  };

  useEffect(() => {
    if (loading || !user) return;
    fetchProfileData(NETWORK);
  }, [loading, user, NETWORK]);

  useEffect(() => {
    if (client?.client_id) {
      fetchWalletAccount(client.client_id);
    }
  }, [client?.client_id]);

  useEffect(() => {
    if (walletAccount?.id) {
      fetchAccountStatement(walletAccount.id);
    }
  }, [walletAccount?.id]);

  const shouldShowStatementLoading =
    statementLoading || (!statementInitialized && (walletAccountLoading || !walletAccount?.id));

  const filteredAccountStatement = useMemo(() => {
    const items = accountStatement || [];

    // Previous implementation kept for reference:
    // return items;
    if (appliedFilter.type === "all") {
      return items;
    }

    if (appliedFilter.type === "range") {
      if (!appliedFilter.start || !appliedFilter.end) return items;

      const rangeStart = new Date(appliedFilter.start);
      rangeStart.setHours(0, 0, 0, 0);
      const rangeEnd = new Date(appliedFilter.end);
      rangeEnd.setHours(23, 59, 59, 999);

      return items.filter((entry) => {
        const entryDate = new Date(entry?.created_at);
        return entryDate >= rangeStart && entryDate <= rangeEnd;
      });
    }

    const monthDate = appliedFilter.month || new Date();
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const monthStart = new Date(year, month, 1);
    const monthEnd = new Date(year, month + 1, 0, 23, 59, 59, 999);

    return items.filter((entry) => {
      const entryDate = new Date(entry?.created_at);
      return entryDate >= monthStart && entryDate <= monthEnd;
    });
  }, [accountStatement, appliedFilter]);

  const handleApplyMonthFilter = () => {
    setAppliedFilter({
      type: "month",
      month: selectedMonth,
    });
  };

  const handleClearMonthFilter = () => {
    setAppliedFilter({
      type: "all",
    });
  };

  const handleApplyRangeFilter = () => {
    setAppliedFilter({
      type: "range",
      start: rangeStartDate,
      end: rangeEndDate,
    });
  };

  const handleClearRangeFilter = () => {
    const currentMonth = new Date();
    setFilterType("month");
    setSelectedMonth(currentMonth);
    setAppliedFilter({
      type: "month",
      month: currentMonth,
    });
  };

  const insights = useMemo(() => {
    const totals = (filteredAccountStatement || []).reduce(
      (acc, entry) => {
        const amount = Number(entry?.amount || 0);
        if (entry?.entry_type === "credit" && entry?.transaction_status === "success") {
          acc.totalCredits += amount;
        }
        if (entry?.entry_type === "debit") {
          acc.totalDebits += amount;
        }
        return acc;
      },
      {
        totalCredits: 0,
        totalDebits: 0,
      },
    );

    return [
      {
        title: "Current Balance",
        value: walletAccount?.balance_display || formatCurrency(walletAccount?.balance_naira || 0),
        subText: walletAccount?.status ? `Account status: ${walletAccount.status}` : "Client account balance",
        icon: CircleDollarSign,
      },
      {
        title: "Total Credits",
        value: formatCurrency(totals.totalCredits),
        subText: "Successful inflows on the account statement",
        icon: ArrowDownLeft,
      },
      {
        title: "Total Debits",
        value: formatCurrency(totals.totalDebits),
        subText: "All debit entries currently on the account statement",
        icon: ArrowUpRight,
      },
    ];
  }, [filteredAccountStatement, walletAccount]);

  const statementColumns = [
    "Name",
    "Type",
    "Entry",
    "Amount",
    "Balance After",
    "Reference",
    "Status",
    "Transaction Date",
  ];

  // Previous implementation kept for reference:
  // const statementTableData = (accountStatement || []).map((entry) => ({
  const statementTableData = (filteredAccountStatement || []).map((entry) => ({
    "Name": entry?.name || "-",
    "Type": formatTransactionType(entry?.transaction_type),
    "Entry": entry?.entry_type ? entry.entry_type.charAt(0).toUpperCase() + entry.entry_type.slice(1) : "-",
    "Amount": formatCurrency(entry?.amount),
    "Balance After": formatCurrency(entry?.balance_after),
    "Reference": entry?.reference || "-",
    "Status": entry?.transaction_status || "-",
    "Transaction Date": formatDateTime(entry?.created_at),
  }));

  const statementPeriodLabel = useMemo(() => {
    if (appliedFilter.type === "range" && appliedFilter.start && appliedFilter.end) {
      return `${formatDateTime(appliedFilter.start)} - ${formatDateTime(appliedFilter.end)}`;
    }
    if (appliedFilter.type === "all") {
      return "All Transactions";
    }
    const monthDate = appliedFilter.month || new Date();
    return monthDate.toLocaleString("en-US", { month: "long", year: "numeric" });
  }, [appliedFilter]);

  const statementFilterControls = (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex items-center gap-2">
        <button
          className={`px-3 py-2 rounded-md text-sm border transition-all ${
            filterType === "month"
              ? "bg-primary-title text-white border-primary-title"
              : "bg-white text-primary-title border-primary-title"
          }`}
          onClick={() => setFilterType("month")}
        >
          Month
        </button>
        <button
          className={`px-3 py-2 rounded-md text-sm border transition-all ${
            filterType === "range"
              ? "bg-primary-title text-white border-primary-title"
              : "bg-white text-primary-title border-primary-title"
          }`}
          onClick={() => setFilterType("range")}
        >
          Range
        </button>
      </div>

      {filterType === "month" ? (
        <SalesMonthFilter
          selectedMonth={selectedMonth}
          onMonthChange={setSelectedMonth}
          onFetch={handleApplyMonthFilter}
          onClear={handleClearMonthFilter}
          isFiltered={appliedFilter.type === "all"}
        />
      ) : (
        <DateRangeFilter
          startDate={rangeStartDate}
          endDate={rangeEndDate}
          onStartDateChange={setRangeStartDate}
          onEndDateChange={setRangeEndDate}
          onFetch={handleApplyRangeFilter}
          onClear={handleClearRangeFilter}
          isFiltered={appliedFilter.type === "range"}
        />
      )}
    </div>
  );

  if (loading) {
    return <PageLoader />;
  }

  return (
    <Column>
      <Row>
        <div className="w-full flex items-center justify-between">
          <div>
            <p className="font-nexa text-primary-title text-sm mb-1">Dashboard</p>
            <h1 className="font-nexa text-2xl text-primary-dark m-0">Account Statement</h1>
          </div>
          <button
            className="flex items-center gap-2 bg-white border border-primary-title text-primary-title px-4 py-2 rounded-md text-sm hover:bg-primary-dark hover:text-white transition-all"
            onClick={() => navigate("/dashboard")}
          >
            <ArrowLeft size={18} /> Back to dashboard
          </button>
        </div>
      </Row>

      <Row>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 w-full">
          {insights.map((card) => (
            <SimpleCard key={card.title} data={card} loading={walletAccountLoading && !walletAccount} />
          ))}
        </div>
      </Row>

      <Row>
        <FullViewTableCard
          title={"All Transactions"}
          data={statementTableData}
          columns={statementColumns}
          loading={shouldShowStatementLoading}
          textFilterColumns={["Name", "Type", "Entry", "Reference", "Status"]}
          monthFilter={statementFilterControls}
          actions={[
            <button
              key="export-pdf"
              className="flex items-center gap-2 bg-white border border-primary-title text-primary-title px-4 py-2 rounded-md text-sm hover:bg-primary-dark hover:text-white transition-all"
              onClick={() =>
                downloadAccountStatementPdf({
                  accountId: walletAccount?.id,
                  transactions: filteredAccountStatement,
                  statementPeriodLabel,
                })
              }
              disabled={shouldShowStatementLoading || !walletAccount?.id}
            >
              <Share size={18} /> Export PDF
            </button>,
          ]}
        />
      </Row>
    </Column>
  );
};

export default WalletStatement;
